import { Fragment, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { fetchListadoCompleto } from '../../../actions/inventarioCarga';

import { CustomValueContainer } from '../../../components/react_select/ReactSelect';
import { generarExcelConteoFisico } from '../../../utils/inventarioConteoExcel';

import ProductoCargaModal from './modals/ProductoCargaModal';
import CarritoPendientes from './CarritoPendientes';

const styles_select = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: (provided) => ({ ...provided }),
    valueContainer: (provided) => ({
        ...provided,
        overflow: 'visible',
        marginTop: '10px'
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: 'absolute',
        top: state.hasValue || state.selectProps.inputValue ? -15 : '0%',
        transition: 'top 0.1s, font-size 0.1s',
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const formatMoney = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '—';
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
};

const obtenerUnidadPrincipal = (producto) => {
    const unidades = Array.isArray(producto?.unidades) ? producto.unidades : [];
    if (!unidades.length) return 'Sin definir';

    const principal = unidades.find((u) => u?.principal || u?.es_principal) || unidades[0];

    return principal?.unidad?.nombre || principal?.nombre || 'Sin definir';
};

const FilaExpandida = ({ data }) => {
    const inventarios = Array.isArray(data?.inventarios) ? data.inventarios : [];
    const proveedores = Array.isArray(data?.proveedores) ? data.proveedores : [];

    return (
        <div className="p-3 bg-light">
            <div className="row">
                <div className="col-12 col-lg-6 mb-3 mb-lg-0">
                    <h6>Inventarios</h6>
                    <div className="table-responsive">
                        <table className="table table-sm table-striped table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>Área</th>
                                    <th>Organizador</th>
                                    <th>Ubicación</th>
                                    <th className="text-end">Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!inventarios.length && (
                                    <tr>
                                        <td colSpan="4" className="text-center">Sin inventarios registrados</td>
                                    </tr>
                                )}

                                {inventarios.map((inv, index) => (
                                    <tr key={inv?._id || inv?.id || index}>
                                        <td>{inv?.area?.nombre || 'Sin definir'}</td>
                                        <td>{inv?.organizador?.nombre || '—'}</td>
                                        <td>{inv?.ubicacion?.nombre || '—'}</td>
                                        <td className="text-end">{inv?.cantidad ?? 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="col-12 col-lg-6">
                    <h6>Proveedores</h6>
                    <div className="table-responsive">
                        <table className="table table-sm table-striped table-bordered mb-0">
                            <thead>
                                <tr>
                                    <th>Proveedor</th>
                                    <th>Código</th>
                                    <th className="text-end">P. público</th>
                                    <th className="text-end">P. mínimo</th>
                                    <th className="text-end">P. liquidación</th>
                                    <th className="text-end">P. histórico</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!proveedores.length && (
                                    <tr>
                                        <td colSpan="6" className="text-center">Sin proveedores registrados</td>
                                    </tr>
                                )}

                                {proveedores.map((prov, index) => (
                                    <tr key={prov?._id || prov?.id || index}>
                                        <td>{prov?.proveedor?.nombre || 'Sin definir'}</td>
                                        <td>{prov?.codigo || 'Sin definir'}</td>
                                        <td className="text-end">{formatMoney(prov?.precio_publico)}</td>
                                        <td className="text-end">{formatMoney(prov?.precio_minimo)}</td>
                                        <td className="text-end">{formatMoney(prov?.precio_liquidacion)}</td>
                                        <td className="text-end">{formatMoney(prov?.precio_historico)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CargaInventario = () => {
    const dispatch = useDispatch();

    const { productos, status, pendingChanges } = useSelector((state) => state.inventarioCarga);

    const [busquedaTexto, setBusquedaTexto] = useState('');

    const [proveedoresSeleccionados, setProveedoresSeleccionados] = useState([]);
    const [ubicacionesSeleccionadas, setUbicacionesSeleccionadas] = useState([]);
    const [soloSinProveedor, setSoloSinProveedor] = useState(false);
    const [soloSinUbicacion, setSoloSinUbicacion] = useState(false);

    const [modalProducto, setModalProducto] = useState(false);
    const [productoEditar, setProductoEditar] = useState(null);

    const opcionesProveedor = useMemo(() => {
        const nombres = new Set();

        (productos || []).forEach((producto) => {
            (producto?.proveedores || []).forEach((prov) => {
                const nombre = prov?.proveedor?.nombre;
                if (nombre) nombres.add(nombre);
            });
        });

        return Array.from(nombres)
            .sort((a, b) => a.localeCompare(b))
            .map((nombre) => ({ value: nombre, label: nombre }));
    }, [productos]);

    const opcionesUbicacion = useMemo(() => {
        const nombres = new Set();

        (productos || []).forEach((producto) => {
            (producto?.inventarios || []).forEach((inv) => {
                const nombre = inv?.area?.nombre;
                if (nombre) nombres.add(nombre);
            });
        });

        return Array.from(nombres)
            .sort((a, b) => a.localeCompare(b))
            .map((nombre) => ({ value: nombre, label: nombre }));
    }, [productos]);

    const productosFiltrados = useMemo(() => {
        const nombresProveedorSeleccionados = (proveedoresSeleccionados || []).map((opt) => opt.value);
        const nombresUbicacionSeleccionados = (ubicacionesSeleccionadas || []).map((opt) => opt.value);
        const textoBusqueda = (busquedaTexto || '').trim().toLowerCase();

        return (productos || []).filter((producto) => {
            const proveedoresProducto = producto?.proveedores || [];
            const inventariosProducto = producto?.inventarios || [];

            if (textoBusqueda) {
                const codigo = (producto?.codigo || '').toString().toLowerCase();
                const nombre = (producto?.nombre || '').toString().toLowerCase();
                if (!codigo.includes(textoBusqueda) && !nombre.includes(textoBusqueda)) return false;
            }

            if (soloSinProveedor && proveedoresProducto.length > 0) return false;
            if (soloSinUbicacion && inventariosProducto.length > 0) return false;

            if (nombresProveedorSeleccionados.length > 0) {
                const nombresProducto = proveedoresProducto.map((prov) => prov?.proveedor?.nombre).filter(Boolean);
                if (!nombresProducto.some((nombre) => nombresProveedorSeleccionados.includes(nombre))) return false;
            }

            if (nombresUbicacionSeleccionados.length > 0) {
                const nombresProducto = inventariosProducto.map((inv) => inv?.area?.nombre).filter(Boolean);
                if (!nombresProducto.some((nombre) => nombresUbicacionSeleccionados.includes(nombre))) return false;
            }

            return true;
        });
    }, [productos, proveedoresSeleccionados, ubicacionesSeleccionadas, soloSinProveedor, soloSinUbicacion, busquedaTexto]);

    const filasTabla = useMemo(() => {
        return (productosFiltrados || []).map((producto) => {
            const cantidadTotal = (producto?.inventarios || []).reduce(
                (acumulado, inv) => acumulado + (Number(inv?.cantidad) || 0),
                0
            );

            return {
                ...producto,
                _cantidadTotal: cantidadTotal
            };
        });
    }, [productosFiltrados]);

    const idsPendientesPorProducto = useMemo(() => {
        const ids = new Set();

        (pendingChanges?.allIds || []).forEach((tempId) => {
            const pendiente = pendingChanges?.byId?.[tempId];
            if (pendiente && pendiente.id_producto) {
                ids.add(pendiente.id_producto);
            }
        });

        return ids;
    }, [pendingChanges]);

    const hayFiltros = useMemo(() => {
        return Boolean(
            busquedaTexto.trim() ||
            proveedoresSeleccionados.length ||
            ubicacionesSeleccionadas.length ||
            soloSinProveedor ||
            soloSinUbicacion
        );
    }, [busquedaTexto, proveedoresSeleccionados, ubicacionesSeleccionadas, soloSinProveedor, soloSinUbicacion]);

    const columns = useMemo(() => ([
        {
            name: 'Código',
            selector: (row) => row?.codigo || 'Sin definir',
            sortable: true,
            wrap: true
        },
        {
            name: 'Nombre',
            selector: (row) => row?.nombre || 'Sin definir',
            sortable: true,
            wrap: true,
            grow: 2,
            cell: (row) => {
                const tienePendiente = idsPendientesPorProducto.has(row?.id || row?._id);
                return (
                    <div className="d-flex align-items-center gap-2">
                        <span>{row?.nombre || 'Sin definir'}</span>
                        {tienePendiente && <span className="badge bg-warning text-dark">Pendiente</span>}
                    </div>
                );
            }
        },
        {
            name: 'Unidad principal',
            selector: (row) => obtenerUnidadPrincipal(row),
            sortable: true,
            wrap: true
        },
        {
            name: 'Cantidad total',
            selector: (row) => row?._cantidadTotal || 0,
            sortable: true,
            cell: (row) => {
                const total = row?._cantidadTotal || 0;
                const className = total === 0 ? 'badge bg-danger' : 'badge bg-success';
                return <span className={className}>{total}</span>;
            }
        },
        {
            name: 'Ubicaciones',
            selector: (row) => (row?.inventarios || []).length,
            sortable: true,
            cell: (row) => {
                const total = (row?.inventarios || []).length;
                const className = total > 0 ? 'badge bg-success' : 'badge bg-danger';
                const texto = total === 0 ? 'Sin ubicación' : total === 1 ? '1 ubicación' : `${total} ubicaciones`;
                return <span className={className}>{texto}</span>;
            }
        },
        {
            name: 'Proveedores',
            selector: (row) => (row?.proveedores || []).length,
            sortable: true,
            cell: (row) => {
                const total = (row?.proveedores || []).length;
                const className = total > 0 ? 'badge bg-success' : 'badge bg-danger';
                const texto = total === 0 ? 'Sin proveedor' : total === 1 ? '1 proveedor' : `${total} proveedores`;
                return <span className={className}>{texto}</span>;
            }
        },
        {
            name: 'Opciones',
            width: '100px',
            cell: (row) => (
                <button
                    type="button"
                    className="btn btn-sm btn-link px-2"
                    title="Editar"
                    onClick={(e) => {
                        e.stopPropagation();
                        setProductoEditar(row);
                        setModalProducto(true);
                    }}
                >
                    <i className="fa fa-edit text-warning" />
                </button>
            )
        }
    ]), [idsPendientesPorProducto]);

    const paginationOptions = {
        rowsPerPageText: 'Filas por página',
        rangeSeparatorText: 'de',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'Todos'
    };

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: true, nombre: 'Carga de Inventario', path: '/inventario/carga' }
        ]));
    }, [dispatch]);

    useEffect(() => {
        if (!Array.isArray(productos) || productos.length === 0) {
            dispatch(fetchListadoCompleto());
        }
    }, [dispatch]);

    const onToggleSinProveedor = () => {
        setSoloSinProveedor((prev) => {
            const next = !prev;
            if (next) setProveedoresSeleccionados([]);
            return next;
        });
    };

    const onToggleSinUbicacion = () => {
        setSoloSinUbicacion((prev) => {
            const next = !prev;
            if (next) setUbicacionesSeleccionadas([]);
            return next;
        });
    };

    const onLimpiarFiltros = () => {
        setBusquedaTexto('');
        setProveedoresSeleccionados([]);
        setUbicacionesSeleccionadas([]);
        setSoloSinProveedor(false);
        setSoloSinUbicacion(false);
    };

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Carga de Inventario</h3>

                    <div className="d-flex justify-content-end gap-2 mb-3">
                        <button
                            type="button"
                            className="btn btn-outline-success"
                            disabled={!productos || productos.length === 0}
                            onClick={() => generarExcelConteoFisico(productos)}
                        >
                            <i className="feather icon-download" /> Descargar listado para conteo físico
                        </button>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                setProductoEditar(null);
                                setModalProducto(true);
                            }}
                        >
                            <i className="feather icon-plus" /> Agregar producto
                        </button>
                    </div>

                    <div className="row mb-3">
                        <div className="col-12 col-lg-3 mb-2 mb-lg-0">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Buscar por código de tienda o nombre..."
                                value={busquedaTexto}
                                onChange={(e) => setBusquedaTexto(e.target.value)}
                            />
                        </div>

                        <div className="col-12 col-lg-3 mb-2 mb-lg-0">
                            <Select
                                name="proveedores"
                                className="select-group"
                                classNamePrefix="react-select"
                                placeholder={'Filtrar por proveedor'}
                                options={opcionesProveedor}
                                value={soloSinProveedor ? [] : proveedoresSeleccionados}
                                isMulti
                                isClearable
                                isDisabled={soloSinProveedor}
                                onChange={(opts) => setProveedoresSeleccionados(opts || [])}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                components={{
                                    ValueContainer: CustomValueContainer
                                }}
                                styles={{ ...styles_select }}
                            />
                        </div>

                        <div className="col-12 col-lg-3 mb-2 mb-lg-0">
                            <Select
                                name="ubicaciones"
                                className="select-group"
                                classNamePrefix="react-select"
                                placeholder={'Filtrar por ubicación física'}
                                options={opcionesUbicacion}
                                value={soloSinUbicacion ? [] : ubicacionesSeleccionadas}
                                isMulti
                                isClearable
                                isDisabled={soloSinUbicacion}
                                onChange={(opts) => setUbicacionesSeleccionadas(opts || [])}
                                menuPortalTarget={document.body}
                                menuPlacement="auto"
                                components={{
                                    ValueContainer: CustomValueContainer
                                }}
                                styles={{ ...styles_select }}
                            />
                        </div>

                        <div className="col-12 col-lg-3 d-flex align-items-center gap-2 flex-wrap">
                            <button
                                type="button"
                                className={soloSinProveedor ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-outline-danger'}
                                onClick={onToggleSinProveedor}
                            >
                                Sin proveedor
                            </button>

                            <button
                                type="button"
                                className={soloSinUbicacion ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-outline-danger'}
                                onClick={onToggleSinUbicacion}
                            >
                                Sin ubicación
                            </button>

                            {hayFiltros && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={onLimpiarFiltros}
                                >
                                    Limpiar filtros
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="mt-2">
                        {status === 'error' && (!productos || productos.length === 0) ? (
                            <div className="alert alert-danger d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <span>No se pudo cargar el inventario.</span>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => dispatch(fetchListadoCompleto())}
                                >
                                    Reintentar
                                </button>
                            </div>
                        ) : (
                            <DataTable
                                columns={columns}
                                data={filasTabla}
                                keyField="id"
                                pagination
                                paginationComponentOptions={paginationOptions}
                                highlightOnHover
                                striped
                                responsive
                                persistTableHead
                                expandableRows
                                expandOnRowClicked
                                expandableRowsComponent={FilaExpandida}
                                progressPending={status === 'cargando' && (!productos || productos.length === 0)}
                                progressComponent={(
                                    <div className="text-center py-4">
                                        <div className="spinner-border" role="status" />
                                        <div className="mt-2">Cargando inventario…</div>
                                    </div>
                                )}
                                noDataComponent={(!productos || productos.length === 0) ? (
                                    <div className="text-center py-4">Aún no hay productos en el inventario</div>
                                ) : (
                                    <div className="text-center py-4">
                                        <div className="mb-2">Ningún producto coincide con los filtros seleccionados</div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={onLimpiarFiltros}
                                        >
                                            Limpiar filtros
                                        </button>
                                    </div>
                                )}
                            />
                        )}
                    </div>
                </div>
            </div>

            <ProductoCargaModal
                show={modalProducto}
                producto={productoEditar}
                onHide={() => {
                    setModalProducto(false);
                    setProductoEditar(null);
                }}
            />

            <CarritoPendientes />
        </Fragment>
    );
};

export default CargaInventario;
